import React, {useEffect, useRef, useState} from "react";
import dayjs from "dayjs";
import {Button, Card, Flex} from "antd";
import type {TableProps} from "antd/es/table";
import {HttpService} from "../../services/http/Http.service";
import FilterForm from "../../components/practices/FilterForm";
import "./Cruscotto.css";
import {
    FileTs,
    FileTsFilter,
    FileTSStatusStatistic,
    NOT_AGGREGATED_STATUSES,
    SendStatusLabel,
    SendStatusType,
    SimulatedFileTS,
    SimulatedFileTSStatisticProjectionDto,
    SimulatorPersonIdPair,
    StatusCodeE,
} from "../../model/models";
import type {SortOrder} from "antd/es/table/interface";
import {useNavigate, useParams} from "react-router-dom";
import CustomTable from "./CustomTable";
import StatusCardList from "./StatusCardList";
import {HttpErrorService} from "../../services/http/HttpError.service";
import {UtilsService} from "../../services/Utils.service";
import Signer from "../../components/firma/Signer";
import PracticeNotify from "../../components/practices/PracticeNotify";
import Archive from "../../components/firma/Archive";
import Suspend from "../../components/suspend/Suspend";
import {DownloadOutlined} from "@ant-design/icons";
import {TemplateUtils} from "../../services/utils/TemplateUtils";
import SignerFile from "../../components/firma/SignerFile";
import PracticeSendNotify from "../../components/practices/PracticeSendNotify";
import CustomSpinner from "../../components/spinner/CustomSpinner";

export default function Cruscotto() {
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const [statistics, setStatistics] = useState<FileTSStatusStatistic[]>([]);
    const [filters, setFilters] = useState<FileTsFilter>({});
    const [selectedStatus, setSelectedStatus] = useState<StatusCodeE | null>(null);
    const [pagination, setPagination] = useState({current: 1, pageSize: 20, total: 0});
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortOrder, setSortOrder] = useState<SortOrder | null>(null);
    const [preview, setPreview] = useState<SimulatedFileTSStatisticProjectionDto>();
    const [selectedRows, setSelectedRows] = useState<SimulatedFileTS[]>([])
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const preselectPairsRef = useRef<SimulatorPersonIdPair[] | null>(null);

    const navigate = useNavigate();
    const {status} = useParams<{ status: string }>();
    const [statisticsAreLoading, setStatisticsAreLoading] = useState(false);
    const [tableIsLoading, setTableIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [tempestaTab, setTempestaTab] = useState<boolean>(false);
    const [isMovingTempesta, setIsMovingTempesta] = useState(false);
    const [isMovingToDaTrasferire, setIsMovingToDaTrasferire] = useState(false);
    const [isTransferring, setIsTransferring] = useState(false);
    // il refetch della tabella dopo il trasferimento parte dal cambio di URL: lo spinner
    // resta aperto finché quel fetch non è concluso
    const awaitingTransferRefetchRef = useRef(false);
    const prevTableIsLoadingRef = useRef(false);
    const TEMPESTA_TAB_STATUSES = [StatusCodeE.SCADUTE, StatusCodeE.DA_TRASFERIRE, StatusCodeE.TRASFERITE];

    function reloadPage(status: string) {
        console.log("Reload page with status:", status);
        const validStatus = Object.values(StatusCodeE).includes(status as StatusCodeE);
        const resolvedStatus = validStatus ? (status as StatusCodeE) : StatusCodeE.DA_LAVORARE;

        if (!validStatus) {
            navigate(`/Cruscotto/${resolvedStatus}`, {replace: true});
            return;
        }

        const updatedFilters = {...filters, statusCode: resolvedStatus};
        setSelectedStatus(resolvedStatus);
        setFilters(updatedFilters);
        setSelectedRows([]);
        setSelectedRowKeys([]);
        if (filters.suspensionMotivation !== undefined) {
            updatedFilters.suspensionMotivation = undefined;
        }

        fetchStatistics(updatedFilters, 0, sortField ?? undefined, sortOrder ?? undefined);
        fetchTable(updatedFilters, 0, sortField ?? undefined, sortOrder ?? undefined);
    }

    const rowKeyOf = (r: SimulatedFileTS) =>
        (selectedStatus && NOT_AGGREGATED_STATUSES.includes(selectedStatus))
            ? (r.index as number)
            : `${r.personId}-${r.simulatorId}`;

    const isFirstMount = useRef(true);

    // 1. Handle Navigation/Validation (Mount Only)
    useEffect(() => {
        const validStatus = Object.values(StatusCodeE).includes(status as StatusCodeE);
        if (!validStatus) {
            navigate(`/Cruscotto/${StatusCodeE.DA_LAVORARE}`, { replace: true });
        }
    }, []); // Only runs once to ensure we are on a valid URL

    // 2. Handle Data Fetching (Whenever status changes)
    useEffect(() => {
        const resolvedStatus = Object.values(StatusCodeE).includes(status as StatusCodeE)
            ? (status as StatusCodeE)
            : StatusCodeE.DA_LAVORARE;

        // Reset local UI state
        setSelectedStatus(resolvedStatus);
        setSelectedRows([]);
        setSelectedRowKeys([]);

        if (TEMPESTA_TAB_STATUSES.includes(resolvedStatus)) {
            setTempestaTab(false);
        }

        const updatedFilters = {
            ...filters,
            statusCode: resolvedStatus,
            suspensionMotivation: undefined,
        };
        setFilters(updatedFilters);

        // Fetch Table (Always happens on status change)
        const tempestaFilter = TEMPESTA_TAB_STATUSES.includes(resolvedStatus) ? false : undefined;
        fetchTable(updatedFilters, 0, sortField ?? undefined, sortOrder ?? undefined, undefined, tempestaFilter);

        // Fetch Statistics (Only on the very first load)
        if (isFirstMount.current) {
            fetchStatistics(updatedFilters, 0, sortField ?? undefined, sortOrder ?? undefined);
            isFirstMount.current = false;
        }
    }, [status]); // Only one dependency to track

    const fetchStatistics = async (
        customFilters: FileTsFilter = {},
        page = 0,
        sortFieldParam?: string,
        sortOrderParam?: SortOrder,
        pageSizeParam: number = pagination.pageSize
    ) => {
        console.debug("Fetching Statistics for Cruscotto");
        const requestBody = {
            ...customFilters,
            selectedCodes: Array.from(customFilters.selectedCodes ?? []),
            selectedYears: Array.from(customFilters.selectedYears ?? [])
        };
        const sortQuery = sortFieldParam ? `&sort=${sortFieldParam},${sortOrderParam === 'descend' ? 'desc' : 'asc'}` : "";
        setStatisticsAreLoading(true);
        try {
            const previewStatisticsData = await
                HttpService.getInstanceAxios().post(`/file-ts/all-statistics-by-status/new?page=${page}&size=${pageSizeParam}${sortQuery}`, requestBody)

            let previewStatistics = previewStatisticsData.data as SimulatedFileTSStatisticProjectionDto;

            setPreview(previewStatistics)
            setStatistics(previewStatistics.fileTSStatusStatistics)
        } catch (error) {
            HttpErrorService.handle("Errore durante il recupero delle statistiche", error, true)
        } finally{
            setStatisticsAreLoading(false);
        }

    }


    const fetchTable = async (
        customFilters: FileTsFilter = {},
        page = 0,
        sortFieldParam?: string,
        sortOrderParam?: SortOrder,
        pageSizeParam: number = pagination.pageSize,
        tempestaFilter?: boolean
    ) => {
        console.debug(`Fetching Table for page ${page}. Status: ${status}`);
        setFilteredData([])
        const requestBody: any = {
            ...customFilters,
            selectedCodes: Array.from(customFilters.selectedCodes ?? []),
            selectedYears: Array.from(customFilters.selectedYears ?? [])
        };
        if (customFilters.statusCode && TEMPESTA_TAB_STATUSES.includes(customFilters.statusCode) && tempestaFilter !== undefined) {
            requestBody.tempesta = tempestaFilter;
        }

        const sortQuery = sortFieldParam ? `&sort=${sortFieldParam},${sortOrderParam === 'descend' ? 'desc' : 'asc'}` : "";
        setTableIsLoading(true);
        try {
            const data = await
                HttpService.getInstanceAxios().post(`/file-ts${status && NOT_AGGREGATED_STATUSES.includes(status) ? '/not-aggregated' : ''}?page=${page}&size=${pageSizeParam}${sortQuery}`, requestBody)

            if (status && NOT_AGGREGATED_STATUSES.includes(status)) {
                let daLavorareFilteredData = data.data.content.map((fileTS: FileTs) => {
                    const res: SimulatedFileTS = {
                        index: fileTS.id,
                        simulatorId: 0,
                        exemptionCodes: [fileTS.exemptionCode],
                        fiscalCode: fileTS.person.fiscalCode,
                        fullName: fileTS.person.lastName + " " + fileTS.person.firstName,
                        hasInstallment: false,
                        hasPayment: false,
                        personId: fileTS.person.id,
                        status: fileTS.status.code,
                        totalInterest: 0,
                        totalSanction: 0,
                        totalSpese: 0,
                        totalSum: 0,
                        totalTicket: fileTS.totalTicket,
                        type: fileTS.type,
                        years: [fileTS.year.toString()],
                        protocolNumbers: [],
                        protocolDate: ""
                    }
                    return res;
                });
                setFilteredData(daLavorareFilteredData);


                if (preselectPairsRef.current) {
                    const toSelect = daLavorareFilteredData.filter((r: SimulatedFileTS) =>
                        preselectPairsRef.current!.some(
                            (p: SimulatorPersonIdPair) => p.personId === r.personId && p.simulatorId === r.simulatorId
                        )
                    );
                    setSelectedRows(toSelect);
                    setSelectedRowKeys(toSelect.map(rowKeyOf));
                    preselectPairsRef.current = null;
                } else {
                    setSelectedRows([]);
                    setSelectedRowKeys([]);
                }
            } else {
                setFilteredData(data.data.content);

                const rows: SimulatedFileTS[] = data.data.content;
                if (preselectPairsRef.current) {
                    const toSelect = rows.filter(r =>
                        preselectPairsRef.current!.some(p => p.personId === r.personId && p.simulatorId === r.simulatorId)
                    );
                    setSelectedRows(toSelect);
                    setSelectedRowKeys(toSelect.map(rowKeyOf));
                    preselectPairsRef.current = null;
                } else {
                    setSelectedRows([]);
                    setSelectedRowKeys([]);
                }
            }

            setPagination({
                current: page + 1,
                pageSize: pageSizeParam,
                total: data.data.totalElements,
            });
        } catch (error) {
            HttpErrorService.handle("Errore durante il recupero dei dati", error, true)
        } finally {
            setTableIsLoading(false);
        }
    };

    const handleTempestaTabChange = (tempesta: boolean) => {
        setTempestaTab(tempesta);
        fetchTable(filters, 0, sortField ?? undefined, sortOrder ?? undefined, undefined, tempesta);
    };

    const handleFilter = (appliedFilters?: FileTsFilter) => {
        if (appliedFilters === undefined) {
            // Se resettiamo i filtri dal form, manteniamo comunque lo stato selezionato
            const stateOnlyFilter: FileTsFilter = {statusCode: selectedStatus};
            setFilters(stateOnlyFilter);
            fetchStatistics(stateOnlyFilter, 0, sortField || undefined, sortOrder || undefined);
            fetchTable(stateOnlyFilter, 0, sortField || undefined, sortOrder || undefined, undefined,
                selectedStatus && TEMPESTA_TAB_STATUSES.includes(selectedStatus) ? tempestaTab : undefined);
        } else {
            // Combiniamo i filtri applicati con lo stato selezionato
            const fullFilters: FileTsFilter = {...appliedFilters, statusCode: selectedStatus};
            setFilters(fullFilters);
            fetchStatistics(fullFilters, 0, sortField || undefined, sortOrder || undefined);
            fetchTable(fullFilters, 0, sortField || undefined, sortOrder || undefined, undefined,
                selectedStatus && TEMPESTA_TAB_STATUSES.includes(selectedStatus) ? tempestaTab : undefined);
        }
    };

    const handleStatusClick = (statusCode: StatusCodeE) => {
        const newStatus = selectedStatus === statusCode ? null : statusCode;
        setSelectedStatus(newStatus);
        setSelectedRows([]);
        setSelectedRowKeys([]);
        if (newStatus) {
            navigate(`/Cruscotto/${newStatus}`);
        }
    };

    const handleTableChange: TableProps<SimulatedFileTS>["onChange"] = (
        paginationConfig,
        _filters,
        sorter
    ) => {
        const order = Array.isArray(sorter) ? sorter[0].order : sorter?.order ?? null;
        const field = Array.isArray(sorter) ? sorter[0].field : sorter?.field;

        const newPageSize = paginationConfig.pageSize ?? pagination.pageSize;

        setSortField(field as string);
        setSortOrder(order ?? null);
        setPagination({
            ...pagination,
            current: paginationConfig.current || 1,
            pageSize: newPageSize
        });
        fetchTable(filters, (paginationConfig.current || 1) - 1, field as string, order, newPageSize,
            selectedStatus && TEMPESTA_TAB_STATUSES.includes(selectedStatus) ? tempestaTab : undefined);
    };

    const exportFilteredToExcel = async () => {
        setIsExporting(true);
        try {
            const requestBody = {
                ...filters,
                selectedCodes: Array.from(filters.selectedCodes ?? []),
                selectedYears: Array.from(filters.selectedYears ?? [])
            };

            const response = await HttpService.getInstanceAxios().post(
                `/file-ts${status && NOT_AGGREGATED_STATUSES.includes(status) ? '/not-aggregated' : ''}?page=0&size=500000`,
                requestBody
            );

            const totalElements = response.data.totalElements;


            if (totalElements > 500000) {
                HttpErrorService.handle("Sono stati trovati più di 500.000 risultati. Solo i primi 500.000 verranno esportati.", undefined, true);
            }

            let rawData: SimulatedFileTS[];

            if (status && NOT_AGGREGATED_STATUSES.includes(status)) {
                const notAggregatedData: FileTs[] = response.data.content;
                rawData = notAggregatedData.map((fileTS): SimulatedFileTS => ({
                    index: fileTS.id,
                    simulatorId: 0,
                    exemptionCodes: [fileTS.exemptionCode],
                    fiscalCode: fileTS.person?.fiscalCode ?? "-",
                    fullName: `${fileTS.person?.lastName ?? ""} ${fileTS.person?.firstName ?? ""}`.trim() || "-",
                    hasInstallment: false,
                    hasPayment: false,
                    personId: fileTS.person?.id ?? 0,
                    status: fileTS.status?.code ?? "-",
                    totalInterest: 0,
                    totalSanction: 0,
                    totalSpese: 0,
                    totalSum: 0,
                    totalTicket: fileTS.totalTicket ?? 0,
                    type: fileTS.type ?? "-",
                    years: [fileTS.year?.toString() ?? "-"],
                    protocolNumbers: [],
                    motivazione: "-",
                    protocolDate: ""
                }));
            } else {
                rawData = response.data.content as SimulatedFileTS[];
            }

            const excelData = rawData.map(d => {
                const baseRow: any = {
                    CODICE_FISCALE: d.fiscalCode ?? "-",
                    COGNOME_NOME: d.fullName ?? "-",
                    CODICE_ESENZIONE: Array.isArray(d.exemptionCodes) && d.exemptionCodes.length > 0 ? d.exemptionCodes.join(" - ") : "-",
                    ANNO: Array.isArray(d.years) && d.years.length > 0 ? d.years.slice().sort((a, b) => parseInt(a) - parseInt(b)).join(" - ") : "-",
                    TIPO: d.type ?? "-",
                    TICKET: d.totalTicket
            };

                if ([StatusCodeE.NOTIFICA, StatusCodeE.ATTESA_PAGAMENTO, StatusCodeE.PAGATE, StatusCodeE.TRASFERITE, StatusCodeE.SCADUTE].includes(selectedStatus!)) {
                    baseRow.NO_PROTOCOLLO = Array.isArray(d.protocolNumbers) && d.protocolNumbers.length > 0 ? d.protocolNumbers.join(" - ") : "-";
                }

                if ([StatusCodeE.NOTIFICA, StatusCodeE.ATTESA_PAGAMENTO, StatusCodeE.PAGATE].includes(selectedStatus!)) {
                    baseRow.STATO_SEND = d.sendStatus && d.sendStatus.length > 0 ? SendStatusLabel[d.sendStatus as SendStatusType] : "-";
                }

                if ([StatusCodeE.ATTESA_PAGAMENTO, StatusCodeE.SCADUTE].includes(selectedStatus!)) {
                    const commDate = (d as any).communicationDate;
                    baseRow.DATA_NOTIFICA = commDate ? dayjs(commDate).format("DD/MM/YYYY") : "-";
                }

                if (selectedStatus === StatusCodeE.SCADUTE) {
                    const dl = (d as any).deadline;
                    baseRow.SCADENZA = dl ? dayjs(dl).format("DD/MM/YYYY") : "-";
                    baseRow.GIORNI = dl ? dayjs().diff(dayjs(dl), 'day') : "-";
                }

                if ([StatusCodeE.SOSPESE, StatusCodeE.RENDICONTATE].includes(selectedStatus!)) {
                    baseRow.MOTIVAZIONE = d.motivazione ?? "-";
                }

                if (selectedStatus === StatusCodeE.DA_RENDICONTARE) {
                    baseRow.DIAGNOSTICA_TS = (d as any).diagnosticoArchiviazioneTS ?? "-";
                }

                return baseRow;
            });

            const xlsx = await import("xlsx");
            const worksheet = xlsx.utils.json_to_sheet(excelData);
            Object.keys(worksheet).forEach((cell) => {
                if (cell.startsWith("F") && cell !== "F1") {
                    worksheet[cell].z = '€ #,##0.00';
                    worksheet[cell].t = 'n';
                }
            });

            const workbook = xlsx.utils.book_new();
            xlsx.utils.book_append_sheet(workbook, worksheet, "Dati");

            xlsx.writeFile(workbook, `export_${selectedStatus ?? 'all'}_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (error) {
            HttpErrorService.handle("Errore durante l'esportazione", error, true);
        } finally {
            setIsExporting(false);
        }
    };

    const personIdPairsToTransfer =  () => {
        return selectedRows.map(row => ({
            simulatorId: row.simulatorId,
            personId: row.personId
        })) as SimulatorPersonIdPair[];
    }

    const getMaxProtocolDateFromSelected = (): Date | null => {
        if (selectedRows.length === 0) return null;
        const protocolDates = selectedRows
            .map(row => row.protocolDate)
            .filter(date => !!date)
            .map(date => new Date(date!));

        if (protocolDates.length === 0) return null;

        return new Date(Math.max(...protocolDates.map(d => d.getTime())));
    };

    const moveTempesta = async (targetTempesta: boolean) => {
        const pairs: SimulatorPersonIdPair[] = selectedRows.map(row => ({
            simulatorId: row.simulatorId,
            personId: row.personId
        }));
        setIsMovingTempesta(true);
        try {
            await HttpService.getInstanceAxios().put(`/file-ts/tempesta/${targetTempesta}`, pairs);
            setSelectedRows([]);
            setSelectedRowKeys([]);
            fetchTable(filters, (pagination.current || 1) - 1, undefined, undefined, pagination.pageSize, tempestaTab);
        } catch (error) {
            HttpErrorService.handle("Errore durante lo spostamento", error, true);
        } finally {
            setIsMovingTempesta(false);
        }
    };

    const moveToDaTrasferire = async () => {
        const pairs: SimulatorPersonIdPair[] = selectedRows.map(row => ({
            simulatorId: row.simulatorId,
            personId: row.personId
        }));
        setIsMovingToDaTrasferire(true);
        try {
            await HttpService.getInstanceAxios().post('/file-ts/da-trasferire', pairs);
            setSelectedRows([]);
            setSelectedRowKeys([]);
            fetchTable(filters, (pagination.current || 1) - 1, undefined, undefined, pagination.pageSize, tempestaTab);
            fetchStatistics();
        } catch (error) {
            HttpErrorService.handle("Errore durante lo spostamento in Da Trasferire", error, true);
        } finally {
            setIsMovingToDaTrasferire(false);
        }
    };

    const transferPractice = async (signedBlob: Blob) => {
        const pairs: SimulatorPersonIdPair[] = selectedRows.map(row => ({
            simulatorId: row.simulatorId,
            personId: row.personId
        }));

        setIsTransferring(true);
        try {
            await TemplateUtils.downloadDocumentsAsZip(pairs,true, signedBlob);
            preselectPairsRef.current = selectedRows.map(sr => ({ simulatorId: sr.simulatorId, personId: sr.personId }));
            // se cambiamo davvero stato, il refetch parte dall'effect su `status`:
            // lo spinner viene chiuso al termine di quel fetch
            const willRefetch = selectedStatus !== StatusCodeE.TRASFERITE;
            awaitingTransferRefetchRef.current = willRefetch;
            handleStatusClick(StatusCodeE.TRASFERITE)
            if (!willRefetch) {
                setIsTransferring(false);
            }
        } catch (error) {
            HttpErrorService.handle("Errore durante il download", error, true)
            awaitingTransferRefetchRef.current = false;
            setIsTransferring(false);
        }
    }

    // chiude lo spinner del trasferimento quando il fetch della tabella passa da in corso a concluso
    useEffect(() => {
        if (prevTableIsLoadingRef.current && !tableIsLoading && awaitingTransferRefetchRef.current) {
            awaitingTransferRefetchRef.current = false;
            setIsTransferring(false);
        }
        prevTableIsLoadingRef.current = tableIsLoading;
    }, [tableIsLoading]);

    return (
        <>

            <Button
                style={{float: "right", marginTop: '27px'}}
                type="primary"
                icon={<DownloadOutlined/>}
                loading={isExporting}
                onClick={() => exportFilteredToExcel()}
            >
                Esporta
            </Button>
            <FilterForm onFilter={handleFilter} selectedStatus={selectedStatus}/>
            <Card
                title={"Riepilogo"}
                className="header-card"
                styles={{
                    header: {
                        background: "#007A33FF",
                        color: "white",
                        paddingBottom: "8px"
                    }
                }}
                loading={statisticsAreLoading}
            >
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: "space-between"}}>
                    {[
                        {label: "N° Posizioni", value: preview?.totalOccurrences},
                        {label: "N° Assistiti", value: preview?.totalPerson},
                        {label: "Tipo", value: preview?.typeList.join(" - ")},
                        {label: "Ticket", value: "€ " + UtilsService.formatNumber(preview?.totalTicket)},
                        {label: "Sanzioni", value: "€ " + UtilsService.formatNumber(preview?.totalSanction)},
                        {label: "Spese", value: "€ " + UtilsService.formatNumber(preview?.totalCosts)},
                        {label: "Interessi", value: "€ " + UtilsService.formatNumber(preview?.totalInterests)},
                        {label: "Totale", value: "€ " + UtilsService.formatNumber(preview?.totalSum)}
                    ].map(({label, value}) => (
                        <div key={label}
                             style={{display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 120}}>
                            <b>{label}</b>
                            <span>{value}</span>
                        </div>
                    ))}
                </div>
            </Card>
            <div className="cruscotto-container">
                <StatusCardList statistics={statistics} selectedStatus={selectedStatus}
                                handleStatusClick={handleStatusClick}
                />
            </div>
            <CustomTable
                data={filteredData}
                pagination={pagination}
                onPaginationChange={(page, pageSize) => {
                    setPagination((prev) => ({...prev, current: page, pageSize}));
                    fetchTable(filters, page - 1, sortField || undefined, sortOrder || undefined, pageSize,
                        selectedStatus && TEMPESTA_TAB_STATUSES.includes(selectedStatus) ? tempestaTab : undefined);
                }}
                onTableChange={handleTableChange}
                sortField={sortField}
                sortOrder={sortOrder}
                selectedStatus={selectedStatus}
                onSelectedRows={(selectedRows) => setSelectedRows(selectedRows)}
                selectedRowKeys={selectedRowKeys}
                onSelectionChange={(keys, rows) => {
                    setSelectedRowKeys(keys as React.Key[]);
                    setSelectedRows(rows as SimulatedFileTS[]);
                }}
                loading={tableIsLoading}
                tempestaTab={tempestaTab}
                onTempestaTabChange={handleTempestaTabChange}
            />
            <Flex justify={"flex-end"} style={{gap: '5px'}}>
                {
                    status === StatusCodeE.DA_FIRMARE && <Signer
                        selectedSimulatorPersonPairs={selectedRows.map(sr => {
                                return {simulatorId: sr.simulatorId, personId: sr.personId} as SimulatorPersonIdPair
                            }
                        )}
                        onSuccess={() => {
                            preselectPairsRef.current = selectedRows.map(sr => ({ simulatorId: sr.simulatorId, personId: sr.personId }));
                            setSelectedRows([]);
                            setSelectedRowKeys([]);
                            handleStatusClick(StatusCodeE.NOTIFICA);
                            fetchStatistics();
                        }}
                    />
                }
                {
                    status === StatusCodeE.NOTIFICA && <PracticeSendNotify
                        title={'Notifica con SEND'}
                        selectedSimulatorPersonPairs={selectedRows.map(sr => {
                                return {simulatorId: sr.simulatorId, personId: sr.personId} as SimulatorPersonIdPair
                            }
                        )}
                        onSuccess={() => {
                            preselectPairsRef.current = selectedRows.map(sr => ({ simulatorId: sr.simulatorId, personId: sr.personId }));
                            reloadPage(StatusCodeE.NOTIFICA)
                            fetchStatistics();
                        }}
                    />
                }
                {
                    status === StatusCodeE.NOTIFICA && <PracticeNotify
                        title={'Notifica manualmente'}
                        selectedSimulatorPersonPairs={selectedRows.map(sr => {
                                return {simulatorId: sr.simulatorId, personId: sr.personId} as SimulatorPersonIdPair
                            }
                        )}
                        onSuccess={() => {
                            preselectPairsRef.current = selectedRows.map(sr => ({ simulatorId: sr.simulatorId, personId: sr.personId }));
                            setSelectedRows([]);
                            setSelectedRowKeys([]);
                            handleStatusClick(StatusCodeE.ATTESA_PAGAMENTO);
                            fetchStatistics();
                        }}
                        minProtocolDate={getMaxProtocolDateFromSelected()}
                    />
                }
                {(status === StatusCodeE.DA_LAVORARE || status === StatusCodeE.IN_LAVORAZIONE || status === StatusCodeE.RESIDUE)   &&
                <Archive
                    selectedSimulatorPersonPairs={status && NOT_AGGREGATED_STATUSES.includes(status) ? [] : selectedRows.map(sr => {
                            return {simulatorId: sr.simulatorId, personId: sr.personId} as SimulatorPersonIdPair
                        }
                    )}
                    selectedFileTsIds={status && NOT_AGGREGATED_STATUSES.includes(status) ? selectedRows.map(sr => {
                        return sr.index ? sr.index : 0
                    }) : []}
                    onSuccess={() => {
                        setSelectedRows([]);
                        setSelectedRowKeys([]);
                        fetchTable(filters, (pagination.current || 1) - 1, undefined, undefined, pagination.pageSize);
                    }}/>
                }
                {
                    status === StatusCodeE.SCADUTE && tempestaTab &&
                    <Button
                        className="custom-label-color"
                        size="large"
                        type="primary"
                        onClick={() => moveTempesta(false)}
                        loading={isMovingTempesta}
                        disabled={selectedRows.length === 0}
                    >
                        Sposta in Diffide
                    </Button>
                }
                {
                    status === StatusCodeE.SCADUTE && !tempestaTab &&
                    <Button
                        className="custom-label-color"
                        size="large"
                        type="primary"
                        onClick={() => moveTempesta(true)}
                        loading={isMovingTempesta}
                        disabled={selectedRows.length === 0}
                    >
                        Sposta in Verbali
                    </Button>
                }
                {
                    status === StatusCodeE.SCADUTE &&
                    <Button
                        size="large"
                        type={"primary"}
                        onClick={moveToDaTrasferire}
                        loading={isMovingToDaTrasferire}
                        disabled={selectedRows.length === 0}
                    >
                        Da Trasferire
                    </Button>
                }
                {
                    status === StatusCodeE.SCADUTE &&
                    <Suspend
                        selectedSimulatorPersonPairs={selectedRows.map(sr => {
                                return {simulatorId: sr.simulatorId, personId: sr.personId} as SimulatorPersonIdPair
                            }
                        )}
                        onSuccess={() => {
                            setSelectedRows([]);
                            setSelectedRowKeys([]);
                            fetchTable(filters, (pagination.current || 1) - 1, undefined, undefined, pagination.pageSize);
                        }}
                    />
                }
                {
                    status === StatusCodeE.DA_TRASFERIRE && !tempestaTab &&
                    <SignerFile selectedSimulatorPersonPairs={personIdPairsToTransfer()}
                                withConfirmationModal={true}
                                buttonName={'Trasferisci'}
                                confirmationModalTitle={'Trasferisci pratiche'}
                                confirmationModalMessage={'Sei sicuro di voler trasferire le diffide?'}
                                onSuccess={transferPractice}
                    />
                }
                {
/*                    status === StatusCodeE.DA_TRASFERIRE && tempestaTab &&
                    <Signer
                        selectedSimulatorPersonPairs={personIdPairsToTransfer()}
                        submitUrlPrefix="/signer/async-process/da-trasferire"
                        buttonLabel="Trasferisci"
                        onSuccess={() => {
                            preselectPairsRef.current = selectedRows.map(sr => ({ simulatorId: sr.simulatorId, personId: sr.personId }));
                            handleStatusClick(StatusCodeE.TRASFERITE);
                        }}
                    />*/
                }
            </Flex>

            <CustomSpinner
                message={"Caricamento in corso..."}
                open={isTransferring}
            />

        </>
    );
}
